import { useEncryptService } from '../shared/hooks';
import axios from 'axios';

/**
 * Encrypt sensitive data for API calls after login
 * @param data - Data to encrypt
 * @returns Encrypted data
 */
export const encryptSensitiveData = (data: any): string => {
  const { doEncrypt } = useEncryptService();
  return doEncrypt(JSON.stringify(data));
};

/**
 * Decrypt sensitive data received from API
 * @param encryptedData - Encrypted data to decrypt
 * @returns Decrypted data
 */
export const decryptSensitiveData = (encryptedData: string): any => {
  const { doDecrypt } = useEncryptService();
  return doDecrypt(encryptedData);
};

/**
 * Encrypt user profile data
 * @param userData - User data to encrypt
 * @returns Encrypted user data
 */
export const encryptUserData = (userData: any): string => {
  const { doEncrypt } = useEncryptService();
  return doEncrypt(JSON.stringify(userData));
};

/**
 * Encrypt API request payload
 * @param payload - Request payload to encrypt
 * @returns Encrypted payload
 */
export const encryptApiPayload = (payload: any): string => {
  const { doEncrypt } = useEncryptService();
  return doEncrypt(JSON.stringify(payload));
};

/**
 * Encrypt file data
 * @param fileData - File data to encrypt
 * @returns Encrypted file data
 */
export const encryptFileData = (fileData: string): string => {
  const { doEncrypt } = useEncryptService();
  return doEncrypt(fileData);
};

/**
 * Split and encrypt large data
 * @param data - Large data to encrypt
 * @param chunkSize - Size of each chunk
 * @returns Array of encrypted chunks
 */
export const encryptLargeData = (data: string, chunkSize: number = 100): string[] => {
  const { doEncrypt, splitStringIntoChunks } = useEncryptService();
  const chunks = splitStringIntoChunks(data, chunkSize);
  return chunks.map(chunk => doEncrypt(chunk));
};

/**
 * Decrypt large data from chunks
 * @param encryptedChunks - Array of encrypted chunks
 * @returns Decrypted data
 */
export const decryptLargeData = (encryptedChunks: string[]): string => {
  const { doDecrypt } = useEncryptService();
  const decryptedChunks = encryptedChunks.map(chunk => doDecrypt(chunk));
  return decryptedChunks.join('');
};

/**
 * Create encrypted API call (POST only)
 * @deprecated Use makeEncryptedApiCall instead
 */
export const createEncryptedApiCall = async (endpoint: string, data: any) => {
  const { getAuthHeader } = useEncryptService();
  const encryptedPayload = encryptApiPayload(data);

  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeader().headers
    },
    body: JSON.stringify({
      encryptedData: encryptedPayload
    })
  });

  return response.json();
};

/**
 * Make an encrypted API call using axiosInstance
 * @param endpoint - API endpoint
 * @param data - Data to encrypt and send
 * @param method - HTTP method (default: POST)
 * @returns API response
 */
export const makeEncryptedApiCall = async (
  endpoint: string,
  data: any,
  method: 'POST' | 'PUT' | 'GET' | 'DELETE' = 'POST'
) => {
  const { doEncrypt, getAuthHeader } = useEncryptService();
  const encryptedPayload = doEncrypt(JSON.stringify(data));

  console.log("=== ENCRYPTION DEBUG ===");
  console.log("Original data:", JSON.stringify(data, null, 2));
  console.log("Encrypted payload length:", encryptedPayload.length);
  console.log("First 100 chars of encrypted:", encryptedPayload.substring(0, 100));
  console.log("========================");

  const authHeaders = getAuthHeader();

  const baseURL = import.meta.env.VITE_SUPERADMIN_BASE_URL?.replace(/\/$/, '');
  const fullUrl = `${baseURL}${endpoint}`;

  const bearerToken = localStorage.getItem('authToken');

  const headers: any = {
    'Content-Type': 'application/json'
  };

  if (bearerToken) {
    headers.Authorization = `Bearer ${bearerToken.replace(/"/g, "")}`;
  } else {
    Object.assign(headers, authHeaders.headers);
  }

  try {
    const response = await axios.request({
      url: fullUrl,
      method,
      headers,
      data: { payload: encryptedPayload }
    });

    return response;
  } catch (error: any) {
    console.error("=== API ERROR DETAILS ===");
    console.error("Error status:", error.response?.status);
    console.error("Error data:", error.response?.data);
    console.error("Error message:", error.message);
    console.error("Request URL:", fullUrl);
    console.error("Request payload:", { payload: encryptedPayload });
    console.error("Request headers:", headers);
    console.error("========================");
    throw error;
  }
};

/**
 * Store encrypted data in localStorage
 */
export const storeEncryptedData = (key: string, data: any): void => {
  const encryptedData = encryptSensitiveData(data);
  localStorage.setItem(key, encryptedData);
};

/**
 * Retrieve and decrypt data from localStorage
 */
export const getEncryptedData = (key: string): any => {
  const encryptedData = localStorage.getItem(key);
  if (encryptedData) {
    return decryptSensitiveData(encryptedData);
  }
  return null;
};
